import { CaretLeft, CaretRight } from '@carbon/icons-react'
import PropTypes from 'prop-types'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination, A11y, Mousewheel } from 'swiper'
import { Tile, Button } from 'carbon-components-react'
import { useCallback, useState, useEffect, useRef } from 'react'
import cx from 'classnames'
import playButton from './assets/play_button.svg'
import watsonAssistant from './utils/watson-assistant'

function ContentCarousel({}) {
  // States for managing carousel functionality
  const [navigationElement, setNavigationElement] = useState()
  const [carouselItems, setCarouselItems] = useState([])
  const [enlargedImage, setEnlargedImage] = useState(null)
  const [loading, setLoading] = useState(true)
  const instance = watsonAssistant.getInstance()
  const videoRefs = useRef({})
  const [playingVideo, setPlayingVideo] = useState(null)

  const BASE_URL = process.env.REACT_APP_BASE_URL // Base URL for backend

  // Fetches authentication token and carousel data on component mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const response = await fetch(`${BASE_URL}/token`)
        if (!response.ok) {
          throw new Error(`Failed to fetch token. Status: ${response.status}`)
        }
        const data = await response.json()
        return data.token
      } catch (error) {
        console.error('Error fetching token:', error)
        throw error
      }
    }

    const fetchCarouselData = async (token) => {
      try {
        setLoading(true)
        const chatHistory = instance.createHistoryObject()

        const response = await fetch(`${BASE_URL}/carousel`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-token': token,
          },
          body: JSON.stringify(chatHistory),
        })
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        setCarouselItems(result.carousel_data)
      } catch (error) {
        console.error('Error fetching carousel data:', error)
      } finally {
        setLoading(false) // End loading
      }
    }

    // Fetch data (token and carousel items)
    const fetchData = async () => {
      try {
        const token = await fetchToken() // Fetch the token first
        await fetchCarouselData(token) // Use the token to fetch carousel data
      } catch (error) {
        console.error('Error during data fetching:', error)
      }
    }
    fetchData()
  }, [BASE_URL, instance])

  // Determines if a file URL corresponds to a video
  const isVideoFile = (url) => url.endsWith('.mp4') || url.endsWith('.webm')

  // Handles clicks on carousel cards
  const onCardClick = useCallback((text) => {
    if (text === 'Show Smart Table') {
      setShowSmartTable(true) // Trigger SmartTable display
    } else {
      instance.sendMessageSilent(`${text}`)
    }
  }, [])

  // Styles for loading spinner and overlay
  const spinnerStyle = {
    border: '3px solid rgba(255, 255, 255, 0.3)',
    borderTop: '3px solid #ffffff',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    animation: 'spin 1.5s linear infinite',
  }

  const spinnerOverlayStyle = {
    position: 'absolute',
    top: '5px',
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
    zIndex: 10,
  }

  return (
    <div
      className="ChatContainer"
      style={{ position: 'relative', marginTop: '1rem', marginBottom: '1rem', minHeight: '30px' }}
    >
      {loading && (
        <div style={spinnerOverlayStyle}>
          <div style={spinnerStyle}></div>
        </div>
      )}

      {!loading && carouselItems.length > 0 && (
        <Swiper
          modules={[Navigation, Pagination, A11y, Mousewheel]}
          keyboard={{ enabled: true }}
          mousewheel={{
            forceToAxis: true,
            sensitivity: 0.5,
            thresholdDelta: 50,
            thresholdTime: 200,
          }}
          touchEventsTarget="container"
          touchRatio={0.5}
          resistance={true}
          resistanceRatio={1}
          pagination={{
            el: navigationElement?.querySelector('.Carousel__BulletContainer') || null,
            clickable: true,
            bulletClass: 'Carousel__Bullet',
            bulletActiveClass: 'Carousel__Bullet--selected',
            renderBullet,
          }}
          navigation={{
            prevEl: navigationElement?.querySelector('button:nth-of-type(1)') || null,
            nextEl: navigationElement?.querySelector('button:nth-of-type(2)') || null,
          }}
          slidesPerView="auto"
          spaceBetween={15}
          centeredSlides
          rewind
        >
          {carouselItems.map((cardData) => {
            const { url, title, alt } = cardData
            const isVideo = isVideoFile(url)

            return (
              <SwiperSlide key={url}>
                <Tile className="Carousel__Card">
                  {!isVideo && (
                    <img
                      className="Carousel__CardImage"
                      src={url}
                      alt={alt}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEnlargedImage(url)}
                    />
                  )}
                  {isVideo && (
                    <video
                      ref={(ref) => (videoRefs.current[url] = ref)}
                      className="Carousel__CardVideo"
                      src={url}
                      controls
                    />
                  )}
                  <div className="Carousel__CardContent">
                    <p className="Carousel__CardTitle">{title}</p>
                  </div>
                  <Button
                    className="Carousel__CardButton Carousel__CardButtonMessage"
                    onClick={() => onCardClick(title)}
                  >
                    View Details
                  </Button>
                </Tile>
              </SwiperSlide>
            )
          })}
        </Swiper>
      )}

      {!loading && (
        <div
          ref={setNavigationElement}
          className={cx('Carousel__Navigation', {
            'Carousel__Navigation--hidden': !carouselItems,
          })}
        >
          <button className="Carousel__NavigationButton">
            <CaretLeft size={16} />
          </button>
          <div className="Carousel__BulletContainer" />
          <button className="Carousel__NavigationButton">
            <CaretRight size={16} />
          </button>
        </div>
      )}

      {enlargedImage && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setEnlargedImage(null)}
        >
          <img
            src={enlargedImage}
            alt="Enlarged"
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              borderRadius: '8px',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
    </div>
  )
}

// Custom bullet renderer for Swiper pagination
function renderBullet(_, className) {
  return `<span class="${className}"></span>`
}
ContentCarousel.propTypes = {
  // Define expected props and their types if necessary
}

export { ContentCarousel }
